import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Spin, Empty, Button, Card, Segmented } from 'antd';
import {
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import eventJobsService, {
  type EventJob,
  type JobStatus,
  type JobType,
  JOB_TYPE_CONFIG,
  JOB_STATUS_CONFIG,
} from '../../api/eventJobsService';
import type { EventWithCircuit } from '../../api/eventsService';

dayjs.extend(utc);
dayjs.extend(timezone);

interface MobileJobsViewProps {
  event: EventWithCircuit;
  theme: 'light' | 'dark';
}

function getStatusIcon(status: JobStatus) {
  switch (status) {
    case 'pending':
      return <ClockCircleOutlined style={{ color: JOB_STATUS_CONFIG.pending.color }} />;
    case 'running':
      return <SyncOutlined spin style={{ color: JOB_STATUS_CONFIG.running.color }} />;
    case 'succeeded':
      return <CheckCircleOutlined style={{ color: JOB_STATUS_CONFIG.succeeded.color }} />;
    case 'failed':
      return <CloseCircleOutlined style={{ color: JOB_STATUS_CONFIG.failed.color }} />;
    default:
      return null;
  }
}

function useEventJobs(event: EventWithCircuit | null) {
  const [jobs, setJobs] = useState<EventJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!event || event.legacy) {
      setJobs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await eventJobsService.getEventJobs(
        event.season_year,
        event.circuit_id,
        event.start_date,
        event.demo ?? false
      );

      if (response.success && response.data) {
        setJobs(response.data);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error('Error fetching mobile jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [event]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  return { jobs, loading, error, refresh: fetchData };
}

type StatusFilter = 'all' | 'pending' | 'running' | 'succeeded' | 'failed';

export const MobileJobsView: React.FC<MobileJobsViewProps> = ({ event, theme }) => {
  const { jobs, loading, error, refresh } = useEventJobs(event);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const isDark = theme === 'dark';
  const tz = event.circuit?.time_zone || 'UTC';

  const statusCounts = useMemo(() => ({
    all: jobs.length,
    pending: jobs.filter((j) => j.status === 'pending').length,
    running: jobs.filter((j) => j.status === 'running').length,
    succeeded: jobs.filter((j) => j.status === 'succeeded').length,
    failed: jobs.filter((j) => j.status === 'failed').length,
  }), [jobs]);

  const filteredAndSortedJobs = useMemo(() => {
    const filtered =
      statusFilter === 'all'
        ? jobs
        : jobs.filter((j) => j.status === statusFilter);
    return [...filtered].sort(
      (a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()
    );
  }, [jobs, statusFilter]);

  const formatTime = (timestamp: string | null) => {
    return eventJobsService.formatTimestamp(timestamp, tz);
  };

  if (event.legacy) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
        }}
      >
        <Empty description="Jobs are not available for legacy events." />
      </div>
    );
  }

  if (loading && jobs.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          gap: 16,
        }}
      >
        <Spin size="large" />
        <span style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' }}>
          Loading jobs…
        </span>
      </div>
    );
  }

  if (error && jobs.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
        }}
      >
        <p style={{ marginBottom: 12 }}>{error}</p>
        <Button icon={<ReloadOutlined />} onClick={refresh}>
          Retry
        </Button>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No jobs for this event" />
      </div>
    );
  }

  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        padding: 16,
      }}
    >
      {/* Header - always visible */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)' }}>
          {filteredAndSortedJobs.length} job{filteredAndSortedJobs.length !== 1 ? 's' : ''}
        </span>
        <Button type="text" icon={<ReloadOutlined />} onClick={refresh} size="small">
          Refresh
        </Button>
      </div>

      {/* Status filter - always visible */}
      <div style={{ flexShrink: 0, marginBottom: 16 }}>
        <Segmented
          value={statusFilter}
          onChange={(val) => setStatusFilter(val as StatusFilter)}
          options={[
            { value: 'all', label: `All (${statusCounts.all})` },
            { value: 'pending', label: `Pending (${statusCounts.pending})` },
            { value: 'running', label: `Running (${statusCounts.running})` },
            { value: 'succeeded', label: `Done (${statusCounts.succeeded})` },
            { value: 'failed', label: `Failed (${statusCounts.failed})` },
          ]}
          block
          size="small"
        />
      </div>

      {/* Scrollable job cards */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {filteredAndSortedJobs.map((job) => {
          const typeConfig = JOB_TYPE_CONFIG[job.job_type as JobType];
          const statusConfig = JOB_STATUS_CONFIG[job.status];
          const details = eventJobsService.getJobConfigDescription(job.job_type, job.job_config);
          return (
            <Card
              key={job.id}
              size="small"
              style={{ backgroundColor: cardBg, borderColor: cardBorder, borderRadius: 10 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Status + Type row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      color: statusConfig?.color ?? undefined,
                    }}
                  >
                    {getStatusIcon(job.status)}
                    {statusConfig?.label ?? job.status}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
                    }}
                  >
                    {typeConfig?.icon} {typeConfig?.label ?? job.job_type}
                  </span>
                </div>

                {/* Scheduled at */}
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: isDark ? '#fff' : '#000',
                  }}
                >
                  Scheduled: {formatTime(job.scheduled_at)}
                </div>

                {/* Details (session, message type, etc.) */}
                {details && (
                  <div
                    style={{
                      fontSize: 13,
                      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                      lineHeight: 1.4,
                    }}
                  >
                    {details}
                  </div>
                )}

                {/* Started / Completed */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 12,
                    color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
                  }}
                >
                  {job.started_at && <span>Started: {formatTime(job.started_at)}</span>}
                  {job.completed_at && <span>Completed: {formatTime(job.completed_at)}</span>}
                </div>

                {/* Error message */}
                {job.error_message && (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#f5222d',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 4,
                    }}
                  >
                    <ExclamationCircleOutlined style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{job.error_message}</span>
                  </div>
                )}

                {/* Job ID (truncated) */}
                <div
                  style={{
                    fontSize: 11,
                    color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                    fontFamily: 'monospace',
                  }}
                >
                  {job.id.slice(0, 8)}…
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
